import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  backfillHotelRoomAssignments,
  fetchHotelRoomNumberChanges,
  fetchPendingHotelConfirmations,
  markHotelRoomsNotified,
} from "../services/hotelAvailabilityService";
import { formatDateDMY } from "../utils/dateFormat";

const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  window.prompt("Copy this value:", text);
};

// WhatsApp wants digits only, with a country code. Caretaker numbers are typed in by hand, so
// anything that is not recognisably an Indian mobile number is left to a manual copy rather than
// being dialled wrongly.
const whatsappNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 11 && digits.startsWith("0")) return `91${digits.slice(1)}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return "";
};

const daysWaiting = (requestedAt) => {
  if (!requestedAt) return null;
  const requested = new Date(requestedAt);
  if (Number.isNaN(requested.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - requested.getTime()) / 86400000));
};

const groupByHotel = (items) => {
  const groups = new Map();
  items.forEach((item) => {
    const key = String(item.hotelId ?? item.hotelName ?? "unknown");
    const group = groups.get(key) || {
      key,
      hotelName: item.hotelName || "Hotel",
      city: item.city || "",
      caretakerName: item.caretakerName || "",
      caretakerNumber: item.caretakerNumber || "",
      bookings: [],
    };
    group.bookings.push(item);
    groups.set(key, group);
  });
  return Array.from(groups.values()).sort((left, right) => {
    const byCity = String(left.city).localeCompare(String(right.city));
    return byCity !== 0 ? byCity : String(left.hotelName).localeCompare(String(right.hotelName));
  });
};

const DIVIDER = "━━━━━━━━━━━━━━━━━━";
// A different glyph from DIVIDER on purpose, so the warning box does not read as another booking
// block further down the message.
const CALLOUT_RULE = "▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬▬";

// One reminder per hotel covering every booking it still owes an acceptance on.
//
// This must not read like the original reservation request - hotels were treating the old wording
// as a fresh booking. So it leads with a REMINDER banner, says outright that nothing new is being
// asked for, and shows how long each booking has been waiting.
//
// Two WhatsApp formatting constraints shape the layout: "1." turns a block into an auto-numbered
// list that discards the leading spaces underneath it, and a leading "-" or "* " becomes a bullet
// list the same way. That is why bookings are separated by bold headers and divider lines rather
// than numbering, indented lines use the "•" character, and every URL sits alone on its own line
// where WhatsApp will link it cleanly.
const reminderText = (group) => {
  const count = group.bookings.length;
  const lines = [
    "*🔔 REMINDER - ACCEPTANCE PENDING*",
    "",
    CALLOUT_RULE,
    // Caps, bold and italic together, boxed and alone on its own line - this is the one sentence
    // the hotel has to read before anything else.
    "*_⚠️ THIS IS NOT A NEW BOOKING REQUEST ⚠️_*",
    CALLOUT_RULE,
    "",
    `Dear ${group.hotelName} Team,`,
    "",
    count === 1
      ? "We had already shared the below booking with you. Your acceptance is still pending."
      : `We had already shared the below ${count} bookings with you. Your acceptance is still pending.`,
    "",
    "Kindly open the acceptance link under each booking and confirm.",
  ];
  group.bookings.forEach((booking) => {
    const waiting = daysWaiting(booking.requestedAt);
    lines.push("");
    lines.push(DIVIDER);
    lines.push(`*BOOKING ID: #${booking.bookingId ?? "-"}*`);
    lines.push(`*Customer:* ${booking.customerName || "-"}`);
    lines.push(`*Guests:* ${booking.noOfTravellers ?? "-"}`);
    lines.push(`*Stay Date:* ${booking.dateLabel || "-"}`);
    const rooms = (booking.roomSummary || "").split("\n").filter(Boolean);
    if (rooms.length) {
      lines.push("*Rooms Required:*");
      rooms.forEach((room) => lines.push(`• ${room}`));
    }
    if (waiting != null) {
      lines.push(`*Waiting since:* ${waiting} day${waiting === 1 ? "" : "s"}`);
    }
    if (booking.confirmationUrl) {
      lines.push("*Tap here to accept:*");
      lines.push(booking.confirmationUrl);
    }
  });
  lines.push(DIVIDER);
  lines.push("");
  lines.push("Once accepted, this booking cannot be denied in future.");
  lines.push("");
  lines.push("Regards,");
  lines.push("*IO Trips*");
  return lines.join("\n");
};

export default function PendingHotelAcceptances() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [roomChanges, setRoomChanges] = useState([]);
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pending, changes] = await Promise.all([
        fetchPendingHotelConfirmations(),
        fetchHotelRoomNumberChanges(),
      ]);
      setItems(pending);
      setRoomChanges(changes);
      setLoaded(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load pending hotel acceptances");
    } finally {
      setLoading(false);
    }
  }, []);

  const runBackfill = useCallback(async () => {
    setBackfilling(true);
    try {
      const result = await backfillHotelRoomAssignments();
      const named = Number(result?.bookingsNamed || 0);
      const considered = Number(result?.bookingsConsidered || 0);
      toast.success(
        named
          ? `Room numbers assigned to ${named} of ${considered} booking${considered === 1 ? "" : "s"} (${result?.roomNightsAssigned || 0} room-nights)`
          : `Checked ${considered} booking${considered === 1 ? "" : "s"} - none needed room numbers`,
      );
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not assign room numbers");
    } finally {
      setBackfilling(false);
    }
  }, [load]);

  const markResent = useCallback(async (confirmationId) => {
    try {
      await markHotelRoomsNotified(confirmationId);
      toast.success("Marked as re-sent");
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not mark as re-sent");
    }
  }, [load]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => groupByHotel(items), [items]);

  const copyReminder = async (group) => {
    try {
      await copyText(reminderText(group));
      toast.success(`Reminder copied for ${group.hotelName}`);
    } catch (error) {
      toast.error("Could not copy the reminder message");
    }
  };

  const copyLink = async (url) => {
    try {
      await copyText(url);
      toast.success("Acceptance link copied");
    } catch (error) {
      toast.error("Could not copy the link");
    }
  };

  return (
    <Card className="mb-3 shadow-sm">
      <Card.Header
        className="d-flex flex-wrap justify-content-between align-items-center gap-2"
        role="button"
        onClick={() => setExpanded((current) => !current)}
      >
        <div>
          <div className="fw-semibold">
            <i className={`bi ${expanded ? "bi-chevron-down" : "bi-chevron-right"} me-2`} aria-hidden="true" />
            Pending Hotel Acceptances
            {loaded && <Badge bg={items.length ? "danger" : "success"} className="ms-2">{items.length}</Badge>}
            {loaded && roomChanges.length > 0 && (
              <Badge bg="warning" text="dark" className="ms-2">
                {roomChanges.length} to re-send
              </Badge>
            )}
          </div>
          <div className="small text-muted">
            Upcoming bookings the hotel has not accepted yet. Copy a reminder and send it on WhatsApp.
          </div>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={backfilling}
            title="Assign room numbers to upcoming bookings made before room numbers were set up"
            onClick={(event) => { event.stopPropagation(); runBackfill(); }}
          >
            {backfilling ? <Spinner animation="border" size="sm" /> : "Assign Room Numbers"}
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={loading}
            onClick={(event) => { event.stopPropagation(); load(); }}
          >
            {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
          </Button>
        </div>
      </Card.Header>
      {expanded && (
        <Card.Body>
          {loading && !loaded && (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" /> Loading...
            </div>
          )}
          {loaded && roomChanges.length > 0 && (
            <div className="border border-warning rounded p-3 mb-3">
              <div className="fw-semibold mb-1">Room numbers changed since the hotel was told</div>
              <div className="small text-muted mb-2">
                These stays were already accepted, but their room numbers have moved since. Re-send the
                reservation message from the booking, then mark it here.
              </div>
              <Table size="sm" responsive className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Booking</th>
                    <th>Hotel</th>
                    <th>Stay Dates</th>
                    <th>Rooms Now</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {roomChanges.map((row) => (
                    <tr key={row.id}>
                      <td className="text-nowrap fw-semibold">#{row.bookingId ?? "-"}</td>
                      <td>
                        <div>{row.hotelName || "Hotel"}</div>
                        <div className="small text-muted">{row.city || ""}</div>
                      </td>
                      <td className="small">{row.dateLabel || "-"}</td>
                      <td className="small" style={{ whiteSpace: "pre-line" }}>{row.roomSummary || "-"}</td>
                      <td className="text-end">
                        <Button size="sm" variant="outline-success" onClick={() => markResent(row.id)}>
                          Mark Re-sent
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          {loaded && !items.length && !roomChanges.length && (
            <Alert variant="success" className="mb-0">
              Every upcoming booking has been accepted by its hotel.
            </Alert>
          )}
          {groups.map((group) => {
            const number = whatsappNumber(group.caretakerNumber);
            return (
              <div key={group.key} className="border rounded p-3 mb-3">
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                  <div>
                    <div className="fw-semibold">
                      {group.hotelName}
                      {group.city ? <span className="text-muted fw-normal"> &mdash; {group.city}</span> : null}
                    </div>
                    <div className="small text-muted">
                      {[group.caretakerName, group.caretakerNumber].filter(Boolean).join(" - ") || "No caretaker contact saved"}
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button size="sm" variant="outline-primary" onClick={() => copyReminder(group)}>
                      <i className="bi bi-clipboard-check me-1" aria-hidden="true" />
                      Copy Reminder
                    </Button>
                    {number ? (
                      <Button
                        size="sm"
                        variant="success"
                        href={`https://wa.me/${number}?text=${encodeURIComponent(reminderText(group))}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <i className="bi bi-whatsapp me-1" aria-hidden="true" />
                        WhatsApp
                      </Button>
                    ) : (
                      <Badge bg="light" text="dark" className="align-self-center border fw-normal">
                        No valid number
                      </Badge>
                    )}
                    <Badge bg="light" text="dark" className="align-self-center border fw-normal">
                      {group.bookings.length} pending
                    </Badge>
                  </div>
                </div>
                <Table responsive size="sm" className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th>Booking</th>
                      <th>Customer</th>
                      <th>Travel Date</th>
                      <th>Stay Dates</th>
                      <th>Rooms</th>
                      <th>Waiting</th>
                      <th className="text-end">Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.bookings.map((booking) => {
                      const waiting = daysWaiting(booking.requestedAt);
                      return (
                        <tr key={booking.id}>
                          <td className="text-nowrap fw-semibold">#{booking.bookingId ?? "-"}</td>
                          <td>
                            <div>{booking.customerName || "-"}</div>
                            <div className="small text-muted">{booking.noOfTravellers ?? "-"} guests</div>
                          </td>
                          <td className="text-nowrap">{booking.travelDate ? formatDateDMY(booking.travelDate) : "-"}</td>
                          <td className="small">{booking.dateLabel || "-"}</td>
                          <td className="small" style={{ whiteSpace: "pre-line" }}>{booking.roomSummary || "-"}</td>
                          <td className="text-nowrap small">
                            {waiting == null ? "-" : `${waiting} day${waiting === 1 ? "" : "s"}`}
                          </td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={!booking.confirmationUrl}
                              onClick={() => copyLink(booking.confirmationUrl)}
                            >
                              Copy Link
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            );
          })}
        </Card.Body>
      )}
    </Card>
  );
}
